import { useEffect, useMemo, useState } from "react";
import "../styles/RegisterPage.css";
import { useNavigate } from "react-router-dom";
import * as Sentry from '@sentry/react';

function Recover() {
    const [recover, setRecover] = useState(false);
    const [user_email, setUserEmail] = useState("");
    const [error_message, setErrorMessage] = useState("");
    const [firstWait, setFirstWait] = useState(false);
    const [loadWait, setLoadWait] = useState(false);
    const [notifyWaiting, setNotifyWaiting] = useState(false);
    const [notifySuccess, setNotifySuccess] = useState("none");

    // NEW: controlled password fields + visibility
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const loadRecoverState = async () => {
            setFirstWait(false);

            try {
                const response = await fetch("/api/change_password");
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data?.error_message || response.statusText || 'Failed to load recovery state');
                }

                if (data.user_email) setUserEmail(data.user_email);
                if (data.recover) setRecover(true);
            } catch (error) {
                Sentry.captureException(error);
                setErrorMessage(`Error: ${error.message || 'Unexpected error occurred.'}`);
            } finally {
                setFirstWait(true);
            }
        };

        loadRecoverState();
    }, []);

    useEffect(() => {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setShowOldPassword(false);
    }, [recover]);


    const triggerRecoverFlow = async () => {
        setLoadWait(true);

        try {
            const result = await fetch("/api/recover", { method: "GET" });
            const data = await result.json();

            if (result.ok) {
                if (data.redirect) {
                    window.location.href = data.redirect;
                } else if (data.error_message) {
                    setErrorMessage(data.error_message);
                }
            } else {
                throw new Error(data?.error_message || result.statusText || 'Failed to start recovery flow');
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error("Recover request failed:", err);
            setErrorMessage(`Error: ${err.message || 'Unexpected error occurred.'}`);
        } finally {
            setLoadWait(false);
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setLoadWait(true);

        try {
            const response = await fetch("/api/change_password", {
                method: "POST",
                body: new FormData(event.target),
            });
            const data = await response.json();

            if (response.ok) {
                if (data.redirect) {
                    window.location.href = data.redirect;
                } else if (data.error_message) {
                    setErrorMessage("Error: " + data.error_message);
                } else {
                    setErrorMessage("Unknown error");
                }
            } else {
                throw new Error(data?.error_message || response.statusText || 'Failed to change password');
            }
        } catch (error) {
            Sentry.captureException(error);
            setErrorMessage(`Error: ${error.message || 'Unexpected error occurred.'}`);
        } finally {
            setLoadWait(false);
        }
    };

    async function notifyPasswordRecoveryError(email) {
        try {
            const response = await fetch("/api/notify_password_recovery_not_received", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result?.error_message || response.statusText || 'Failed to notify support');
            }

            if (result.status === "success") {
                setNotifySuccess("success");
                setNotifyWaiting(false);
            } else {
                setNotifySuccess("error");
                setNotifyWaiting(false);
            }
        } catch (error) {
            Sentry.captureException(error);
            setNotifySuccess("error");
            setNotifyWaiting(false);
        }
    }

    const headerTitle = recover ? "Recover Password" : "Change Password";

    // NEW: password rules (same as RegisterPanel)
    const passwordChecks = useMemo(() => {
        const min = password.length >= 8;
        const upper = /[A-Z]/.test(password);
        const special = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const match =
            password.length > 0 &&
            confirmPassword.length > 0 &&
            password === confirmPassword;

        return { min, upper, special, match };
    }, [password, confirmPassword]);

    const passwordRulesMain = [
        { key: "min", label: "8+ total characters", ok: passwordChecks.min },
        { key: "upper", label: "1+ uppercase letter", ok: passwordChecks.upper },
        { key: "special", label: "1+ special character", ok: passwordChecks.special },
    ];

    const passwordRuleMatch = {
        key: "match",
        label: "Passwords match",
        ok: passwordChecks.match,
    };

    // Helper: when switching between recover/change states, clear password fields
    useEffect(() => {
        setPassword("");
        setConfirmPassword("");
        setShowPassword(false);
        setShowConfirmPassword(false);
    }, [recover]);

    return (
        <div className="register-page">
            <div className="register-shell">
                <div className="register-card">
                    <div className="register-header">
                        <div className="register-header-left">
                            <button
                                className="back-btn"
                                onClick={() => navigate("/")}
                                type="button"
                            >
                                ← Back
                            </button>
                        </div>

                        <div className="register-header-center">
                            <h1>{headerTitle}</h1>
                        </div>

                        <div className="register-header-right" />
                    </div>

                    {!firstWait && (
                        <div className="center-row">
                            <div className="spinner-black" />
                        </div>
                    )}

                    {firstWait && (
                        <>
                            {recover ? (
                                <>
                                    <p className="muted center-text">
                                        Enter the recovery code sent to{" "}
                                        <span className="mono">{user_email}</span>.
                                    </p>
                                    <p className="muted center-text">
                                        It may take up to a minute or two to receive the email.
                                    </p>

                                    <form
                                        method="post"
                                        className="register-form"
                                        onSubmit={onSubmit}
                                    >
                                        <input type="hidden" name="type" value="recover" />

                                        <div className="field">
                                            <label>Recovery Code</label>
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                name="code"
                                                aria-label="Recovery code"
                                                placeholder="6-digit code"
                                                required
                                                maxLength={6}
                                                minLength={6}
                                            />
                                        </div>

                                        {/* NEW PASSWORD (with eye) */}
                                        <div className="field">
                                            <label>New Password</label>

                                            <div className="password-wrap">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    aria-label="New password"
                                                    placeholder="New Password"
                                                    required
                                                    minLength={8}
                                                    maxLength={40}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                />

                                                <button
                                                    type="button"
                                                    className="password-eye"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    onClick={() => setShowPassword((v) => !v)}
                                                >
                                                    <img
                                                        src={
                                                            showPassword
                                                                ? "/static/icons/eye-closed.png"
                                                                : "/static/icons/eye-open.png"
                                                        }
                                                        alt=""
                                                        className="password-eye-icon"
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        {/* RULES */}
                                        <div className="password-rules">
                                            {passwordRulesMain.map((rule) => (
                                                <div
                                                    key={rule.key}
                                                    className={`password-rule ${rule.ok ? "ok" : ""}`}
                                                >
                                                    <span className="rule-icon" aria-hidden="true">
                                                        {rule.ok ? "✓" : "•"}
                                                    </span>
                                                    <span>{rule.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* CONFIRM (with eye) */}
                                        <div className="field">
                                            <label>Confirm New Password</label>

                                            <div className="password-wrap">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirm_password"
                                                    aria-label="Confirm new password"
                                                    placeholder="Confirm New Password"
                                                    required
                                                    minLength={8}
                                                    maxLength={40}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                />

                                                <button
                                                    type="button"
                                                    className="password-eye"
                                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                                >
                                                    <img
                                                        src={
                                                            showConfirmPassword
                                                                ? "/static/icons/eye-closed.png"
                                                                : "/static/icons/eye-open.png"
                                                        }
                                                        alt=""
                                                        className="password-eye-icon"
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="password-rules password-rules-match">
                                            <div className={`password-rule ${passwordRuleMatch.ok ? "ok" : ""}`}>
                                                <span className="rule-icon" aria-hidden="true">
                                                    {passwordRuleMatch.ok ? "✓" : "•"}
                                                </span>
                                                <span>{passwordRuleMatch.label}</span>
                                            </div>
                                        </div>

                                        {error_message.length >= 1 && (
                                            <div className="error-box">{error_message}</div>
                                        )}

                                        <button
                                            type="submit"
                                            className="primary-btn"
                                            disabled={loadWait}
                                        >
                                            {loadWait ? (
                                                <span className="btn-inline">
                                                    <span className="spinner" />
                                                </span>
                                            ) : (
                                                "Confirm"
                                            )}
                                        </button>
                                    </form>

                                    <button
                                        className="link-btn"
                                        type="button"
                                        onClick={triggerRecoverFlow}
                                        disabled={loadWait}
                                    >
                                        Send Code Again
                                    </button>

                                    <div className="register-divider" />

                                    <div className="support-row">
                                        {notifySuccess !== "error" && notifySuccess !== "success" && (
                                            <>
                                                <span className="support-text">Not receiving email?</span>

                                                {!notifyWaiting && (
                                                    <button
                                                        type="button"
                                                        className="support-button"
                                                        disabled={notifyWaiting}
                                                        onClick={() => {
                                                            setNotifyWaiting(true);
                                                            notifyPasswordRecoveryError(user_email);
                                                        }}
                                                    >
                                                        Notify Support
                                                    </button>
                                                )}

                                                {notifyWaiting && <span className="support-spinner" />}

                                                <span className="tooltip-container">
                                                    <span className="tooltip-icon">?</span>
                                                    <span className="tooltip-text">
                                                        Sends a support notification to the Rezify team to help resolve password recovery email issues.
                                                        Your email address ({user_email}) will be included in the support request, so we can assist you directly.
                                                    </span>
                                                </span>
                                            </>
                                        )}

                                        {notifySuccess === "error" && (
                                            <span className="support-text">
                                                Error sending notification. Please try again later, or email us directly at support@rezify.ai
                                            </span>
                                        )}

                                        {notifySuccess === "success" && (
                                            <span className="support-text">
                                                Support has been notified! We will be at your assistance shortly.
                                            </span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="muted center-text">
                                        Change password for{" "}
                                        <span className="mono">{user_email}</span>.
                                    </p>

                                    <form
                                        method="post"
                                        className="register-form"
                                        onSubmit={onSubmit}
                                    >
                                        <input type="hidden" name="type" value="change" />

                                        <div className="field">
                                          <label>Current Password</label>

                                          <div className="password-wrap">
                                            <input
                                              type={showOldPassword ? "text" : "password"}
                                              name="old_password"
                                              aria-label="Current password"
                                              placeholder="Current Password"
                                              required
                                              maxLength={40}
                                            />

                                            <button
                                              type="button"
                                              className="password-eye"
                                              aria-label={showOldPassword ? "Hide current password" : "Show current password"}
                                              onClick={() => setShowOldPassword((v) => !v)}
                                            >
                                              <img
                                                src={
                                                  showOldPassword
                                                    ? "/static/icons/eye-closed.png"
                                                    : "/static/icons/eye-open.png"
                                                }
                                                alt=""
                                                className="password-eye-icon"
                                              />
                                            </button>
                                          </div>
                                        </div>


                                        {/* NEW PASSWORD (with eye) */}
                                        <div className="field">
                                            <label>New Password</label>

                                            <div className="password-wrap">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    aria-label="New password"
                                                    placeholder="New Password"
                                                    required
                                                    minLength={8}
                                                    maxLength={40}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                />

                                                <button
                                                    type="button"
                                                    className="password-eye"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    onClick={() => setShowPassword((v) => !v)}
                                                >
                                                    <img
                                                        src={
                                                            showPassword
                                                                ? "/static/icons/eye-closed.png"
                                                                : "/static/icons/eye-open.png"
                                                        }
                                                        alt=""
                                                        className="password-eye-icon"
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        {/* RULES */}
                                        <div className="password-rules">
                                            {passwordRulesMain.map((rule) => (
                                                <div
                                                    key={rule.key}
                                                    className={`password-rule ${rule.ok ? "ok" : ""}`}
                                                >
                                                    <span className="rule-icon" aria-hidden="true">
                                                        {rule.ok ? "✓" : "•"}
                                                    </span>
                                                    <span>{rule.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* CONFIRM (with eye) */}
                                        <div className="field">
                                            <label>Confirm New Password</label>

                                            <div className="password-wrap">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirm_password"
                                                    aria-label="Confirm new password"
                                                    placeholder="Confirm New Password"
                                                    required
                                                    minLength={8}
                                                    maxLength={40}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                />

                                                <button
                                                    type="button"
                                                    className="password-eye"
                                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                                >
                                                    <img
                                                        src={
                                                            showConfirmPassword
                                                                ? "/static/icons/eye-closed.png"
                                                                : "/static/icons/eye-open.png"
                                                        }
                                                        alt=""
                                                        className="password-eye-icon"
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="password-rules password-rules-match">
                                            <div className={`password-rule ${passwordRuleMatch.ok ? "ok" : ""}`}>
                                                <span className="rule-icon" aria-hidden="true">
                                                    {passwordRuleMatch.ok ? "✓" : "•"}
                                                </span>
                                                <span>{passwordRuleMatch.label}</span>
                                            </div>
                                        </div>

                                        {error_message.length >= 1 && (
                                            <div className="error-box">{error_message}</div>
                                        )}

                                        <button
                                            type="submit"
                                            className="primary-btn"
                                            disabled={loadWait}
                                        >
                                            {loadWait ? (
                                                <span className="btn-inline">
                                                    <span className="spinner" />
                                                </span>
                                            ) : (
                                                "Confirm"
                                            )}
                                        </button>

                                        <button
                                            className="link-btn"
                                            type="button"
                                            onClick={triggerRecoverFlow}
                                            disabled={loadWait}
                                        >
                                            Forgot Password?
                                        </button>
                                    </form>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export { Recover };
